import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Crown, Check, TicketPercent, MailCheck, Wrench } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

export default function Join() {
    const { user, refresh } = useAuth();
    const nav = useNavigate();
    const [busy, setBusy] = useState(false);

    const join = async () => {
        if (!user) {
            nav('/register?join=1');
            return;
        }
        setBusy(true);
        try {
            await api.post('/membership/join');
            await refresh();
            toast.success('Welcome to RRM!');
        } catch {
            toast.error('Could not activate membership');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="text-center">
                <div className="inline-flex items-center gap-2 text-xs px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 tracking-widest">
                    <Crown className="w-3.5 h-3.5" /> RRM CLUB
                </div>
                <h1 className="mt-4 font-display text-5xl md:text-6xl text-white">JOIN THE CLUB</h1>
                <p className="mt-3 text-white/60 max-w-2xl mx-auto">Membership unlocks club coupons, early access to new drops, and our upcoming newsletter packed with build features & technical write-ups.</p>
            </div>

            <div className="mt-10 grid md:grid-cols-3 gap-4">
                {[
                    { title: 'Club Coupons', body: 'Exclusive members-only discount codes on OEM and upgrade parts.', icon: TicketPercent },
                    { title: 'Future Newsletter', body: 'Builds, install guides, & tech features delivered monthly.', icon: MailCheck },
                    { title: 'Member fitment support', body: 'Priority help via AI Mechanic + human-routed escalations.', icon: Wrench },
                ].map((c) => (
                    <div key={c.title} className="rrm-panel p-5">
                        <c.icon className="w-6 h-6 text-amber-400" />
                        <div className="mt-3 font-display text-xl text-white">{c.title}</div>
                        <div className="mt-1 text-sm text-white/60">{c.body}</div>
                    </div>
                ))}
            </div>

            <div className="mt-10 rrm-panel p-6 md:p-8 grid md:grid-cols-[1fr_auto] items-center gap-6">
                <div>
                    <div className="font-display text-3xl text-white">FREE TO JOIN</div>
                    <div className="text-white/60 text-sm mt-1">No fees today. Your account stores your saved vehicles & order history.</div>
                    <ul className="mt-4 grid sm:grid-cols-2 gap-2 text-sm text-white/70">
                        <Bullet>Member coupons</Bullet>
                        <Bullet>Newsletter (coming soon)</Bullet>
                        <Bullet>Early access drops</Bullet>
                        <Bullet>Priority fitment help</Bullet>
                    </ul>
                </div>
                <div className="flex flex-col gap-3">
                    {user?.is_member ? (
                        <div className="text-emerald-300 text-sm inline-flex items-center gap-2"><Check className="w-4 h-4" /> You're a member</div>
                    ) : (
                        <button
                            onClick={join}
                            disabled={busy}
                            data-testid="join-membership-primary-button"
                            className="px-6 py-3 rounded-md bg-[var(--rrm-red)] hover:bg-[var(--rrm-red2)] text-white font-semibold inline-flex items-center gap-2 disabled:opacity-50"
                        >
                            <Crown className="w-4 h-4" /> {user ? 'Activate membership' : 'Create account & Join'}
                        </button>
                    )}
                    {!user && (
                        <Link to="/login" className="text-xs text-white/60 hover:text-white text-center">Already have an account? Sign in</Link>
                    )}
                </div>
            </div>
        </div>
    );
}

function Bullet({ children }) {
    return <li className="inline-flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> {children}</li>;
}
